import React from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import A11yFocus from '@ids/a11yfocus';
import RcCheckbox from '@ids/rc-checkbox';
import { themes, getTheme, ThemeContext } from '@ids/theme';
import InlineValidationMessage, {
  InlineValidationType
} from '@ids/inline-validation-message';
import { WrapperSpan, Label } from './StyledCheckbox';

const { bool, func, node, string, oneOf } = PropTypes;

/** */
export default class Checkbox extends React.PureComponent {
  static contextType = ThemeContext;

  static propTypes = {
    /**
     * Specifies  whether the checkbox is selected.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    checked: bool,
    /**
     * The Checkbox label
     */
    children: node,
    /**
     * Specifies the initial state: whether or not the checkbox is selected.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    defaultChecked: bool,
    /**
     * Disable the checkbox.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Specifies if checkbox is in a indeterminate state [-].
     */
    // eslint-disable-next-line react/boolean-prop-naming
    indeterminate: bool,
    /**
     * Checkbox current name.
     */
    name: string,
    /**
     * Callback function that is fired when a Checkbox has been selected. If part of a
     * CheckboxGroup, use that onChange instead.
     */
    onChange: func,
    /**
     * The application's ThemeProvider determines the default theme the component
     * will use. Use theme prop to override (e.g. 'mint', 'proconnect', 'quickbooks',
     * 'turbotax').
     */
    theme: oneOf(themes),
    /**
     * Checkbox current value.
     */
    value: string,
    /**
     * Validation Type
     */
    validationType: InlineValidationType,
    /**
     * Validation Message
     */
    validationMessage: string,
    /**
     * The aria-label property surfaces a label to assistive technology. Use it to replace the text node for accessibility.
     */
    // eslint-disable-next-line react/require-default-props
    'aria-label': string,
    // eslint-disable-next-line react/require-default-props
    'aria-describedby': string
  };

  static defaultProps = {
    checked: undefined,
    children: null,
    defaultChecked: false,
    disabled: false,
    indeterminate: false,
    name: undefined,
    onChange() {},
    theme: undefined,
    value: undefined,
    validationMessage: '',
    validationType: ''
  };

  assignInputRef = c => {
    this.inputRef = c;
  };

  render() {
    const {
      children,
      disabled,
      indeterminate,
      theme,
      validationMessage,
      validationType,
      'aria-describedby': ariaDescribedBy,
      ...restProps
    } = this.props;

    const ariaId =
      ariaDescribedBy ||
      (validationMessage && validationType && uniqueId('checkbox-aria')) ||
      undefined;

    const { currentTheme } = getTheme({ theme }, this.context);

    return (
      <>
        <Label disabled={disabled} theme={currentTheme}>
          <A11yFocus passA11yFocusProp>
            <RcCheckbox
              aria-describedby={ariaId}
              disabled={disabled}
              indeterminate={indeterminate}
              validationType={validationType}
              validationMessage={validationMessage}
              assignInputRef={this.assignInputRef}
              theme={currentTheme}
              {...restProps}
            />
          </A11yFocus>
          {children && (
            <WrapperSpan disabled={disabled} theme={currentTheme}>
              {children}
            </WrapperSpan>
          )}
        </Label>
        <InlineValidationMessage
          id={ariaDescribedBy ? undefined : ariaId}
          message={validationMessage}
          type={validationType}
        />
      </>
    );
  }
}
